import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, Package, FileText, Settings, LogOut, 
  Upload, Image, Menu, X, TrendingUp, Mail, FlaskConical
} from 'lucide-react';
import peptLogo from '../../assets/pept-logo-cropped.png';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const statCards = [
    { label: 'Total Peptides', value: stats?.total_peptides || 0, icon: FlaskConical, color: 'bg-blue-600' },
    { label: 'COA Certificates', value: stats?.total_coas || 0, icon: FileText, color: 'bg-green-600' },
    { label: 'With Images', value: stats?.peptides_with_images || 0, icon: Image, color: 'bg-purple-600' },
    { label: 'Contact Messages', value: stats?.total_contacts || 0, icon: Mail, color: 'bg-orange-600' },
  ];

  const menuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { label: 'Peptides', icon: Package, path: '/admin/peptides' },
    { label: 'COA Library', icon: FileText, path: '/admin/coas' },
    { label: 'Site Settings', icon: Settings, path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen bg-neutral-900 flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-neutral-800 border-r border-neutral-700 transform transition-transform lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4 border-b border-neutral-700">
          <img src={peptLogo} alt="PEP-T.COM" className="h-10" />
          <p className="text-neutral-400 text-xs mt-1">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-3 px-4 py-3 text-neutral-300 hover:bg-neutral-700 hover:text-white rounded-sm transition-colors"
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-neutral-400 hover:text-red-400 w-full transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Top bar */}
        <header className="bg-neutral-800 border-b border-neutral-700 px-4 py-4 flex items-center justify-between lg:px-6">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-neutral-400 hover:text-white lg:hidden"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Dashboard
          </h1>
          <Link to="/" target="_blank" className="text-sm text-neutral-400 hover:text-white">
            View Site →
          </Link>
        </header>

        {/* Dashboard content */}
        <div className="p-4 lg:p-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((stat, index) => (
              <div key={index} className="bg-neutral-800 border border-neutral-700 rounded-sm p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${stat.color} rounded-sm`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-sm text-neutral-400">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="bg-neutral-800 border border-neutral-700 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/admin/peptides"
                className="p-4 bg-neutral-700 hover:bg-neutral-600 rounded-sm text-center transition-colors"
              >
                <Package className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-white font-medium">Manage Peptides</p>
              </Link>
              <Link
                to="/admin/coas"
                className="p-4 bg-neutral-700 hover:bg-neutral-600 rounded-sm text-center transition-colors"
              >
                <FileText className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-white font-medium">Upload COA</p>
              </Link>
              <Link
                to="/admin/settings"
                className="p-4 bg-neutral-700 hover:bg-neutral-600 rounded-sm text-center transition-colors"
              >
                <Image className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="text-white font-medium">Change Hero</p>
              </Link>
              <Link
                to="/admin/settings"
                className="p-4 bg-neutral-700 hover:bg-neutral-600 rounded-sm text-center transition-colors"
              >
                <Settings className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <p className="text-white font-medium">Site Settings</p>
              </Link>
            </div>
          </div>

          {/* Info */}
          <div className="bg-neutral-800 border border-neutral-700 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Getting Started
            </h2>
            <ul className="space-y-2 text-neutral-300 text-sm">
              <li>• <strong>Peptides:</strong> Add, edit, or remove peptides from your catalog</li>
              <li>• <strong>COA Library:</strong> Upload new Certificate of Analysis images</li>
              <li>• <strong>Site Settings:</strong> Change hero image, contact info, and more</li>
              <li>• <strong>Images:</strong> Upload vial photos for individual products</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
}
